/**
 * WorkDeskUtilityBar — Phase 5 (D5, K3 pixel-pass 2026-08-22).
 *
 * The chromeless WorkDesk family (K3 D5 ruling: no AppLayout, no left nav —
 * a full-bleed single-locus terminal) still needs the navigation the app
 * shell would otherwise provide: refresh, settings, identity, sign-out.
 * This slim `h-9` strip is that minimal, family-level closure — NOT app
 * chrome. It carries a `roleLabel` prop so every WorkDesk surface
 * (accountant now; analyst/attorney/admin in a follow-up) can mount the
 * same bar instead of drifting into per-surface one-offs.
 *
 * Design law: chromeless terminal, one accent. Orange appears NOWHERE in
 * this bar at rest — every icon/label sits at `text-text-dim`, brightening
 * only on hover/focus (`text-text-secondary`), never orange. `bg-bg-base`
 * is a dead Tailwind class (Code-fact #1, K3 pixel-pass) — the strip's
 * background is the inline CSS var form.
 *
 * Identity + sign-out reuse the SAME mechanism AppLayout.tsx already uses
 * (`useUser`/`useClerk` from TestAuthContext, which wraps Clerk in
 * production and a mock in REACT_APP_DISABLE_AUTH test mode) — no new auth
 * path is invented here. Both are sourced internally by default so a
 * consuming WorkDesk page needs zero auth wiring of its own; `userName` /
 * `roleTag` / `onLogout` stay as escape-hatch overrides for tests and for
 * a future surface that already has this data in hand.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Settings, LogOut } from 'lucide-react';
import { useUser, useClerk } from '../../contexts/TestAuthContext';
import { BRAND_NAME } from '../../config/brand';

export interface WorkDeskUtilityBarProps {
  /** e.g. "Accountant WorkDesk" — rendered as "CLAIMGENIUS / ACCOUNTANT WORKDESK". */
  roleLabel: string;
  onRefresh: () => void;
  isRefreshing: boolean;
  /** Override for the displayed name; defaults to the signed-in user's fullName/email. */
  userName?: string;
  /** Override for the role tag under the name; defaults to the first word of roleLabel. */
  roleTag?: string;
  /** Override for the sign-out handler; defaults to useClerk().signOut(). */
  onLogout?: () => void;
}

const utilityItemClass =
  'h-9 px-2.5 flex items-center justify-center text-text-dim hover:text-text-secondary transition-colors duration-150 rounded-[2px] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand';

export default function WorkDeskUtilityBar({
  roleLabel,
  onRefresh,
  isRefreshing,
  userName,
  roleTag,
  onLogout,
}: WorkDeskUtilityBarProps) {
  const navigate = useNavigate();
  // Reused mechanism (see file header) — always called (rules-of-hooks);
  // only USED when the caller doesn't override via props.
  const { user } = useUser();
  const { signOut } = useClerk();

  const resolvedName =
    userName ?? ((user as any)?.fullName || (user as any)?.primaryEmailAddress?.emailAddress || 'User');
  const resolvedRoleTag = roleTag ?? roleLabel.split(' ')[0].toUpperCase();
  const handleLogout = onLogout ?? (() => signOut());

  return (
    <div
      className="h-9 w-full flex items-center justify-between px-4 border-b border-border mb-4 sticky top-0 z-40 lg:static lg:z-auto lg:shrink-0"
      style={{ backgroundColor: 'var(--color-bg-base)' }}
      data-testid="workdesk-utility-bar"
    >
      {/* Left — breadcrumb identity, not a link farm */}
      <div className="flex items-center gap-1 min-w-0">
        <span className="text-[11px] font-semibold tracking-[0.14em] text-text-primary uppercase truncate">
          {BRAND_NAME}
        </span>
        <span className="text-[11px] tracking-[0.14em] text-text-dim uppercase truncate">
          / {roleLabel}
        </span>
      </div>

      {/* Right cluster — refresh, settings, divider, user + logout */}
      <div className="flex items-center gap-0.5 shrink-0">
        <button
          type="button"
          onClick={onRefresh}
          aria-label="Refresh"
          title="Refresh"
          data-testid="workdesk-utility-refresh"
          className={utilityItemClass}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin' : ''}`} aria-hidden="true" />
        </button>

        <button
          type="button"
          onClick={() => navigate('/settings')}
          aria-label="Settings"
          title="Settings"
          data-testid="workdesk-utility-settings"
          className={utilityItemClass}
        >
          <Settings className="w-3.5 h-3.5" aria-hidden="true" />
        </button>

        <div aria-hidden="true" className="w-px h-4 bg-border mx-1.5" />

        <div className="h-9 flex items-center gap-2 pl-1">
          <div className="flex flex-col items-end leading-tight max-w-[9rem]">
            <span className="text-xs text-text-muted truncate w-full text-right">{resolvedName}</span>
            <span className="text-[10px] tracking-wider text-text-dim uppercase truncate w-full text-right">
              {resolvedRoleTag}
            </span>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            aria-label="Log out"
            title="Log out"
            data-testid="workdesk-utility-logout"
            className={`${utilityItemClass} -ml-1`}
          >
            <LogOut className="w-3.5 h-3.5" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}
